import React, { PropsWithChildren, ReactNode } from 'react'
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { SxProps, styled } from '@mui/system'
import Title from 'components/global/Title'
import BaseModal from 'components/Modal/BaseModal'
import { ActionIcon } from 'components/Modal/ActionModal'

export interface IModalProps {
  title?: string
  header?: () => JSX.Element | null
  onClose?: () => void
  closeIconSx?: SxProps
  icon?: ReactNode
  height?: string
  maxWidth?: string
  minWidth?: string
  minHeight?: string
  maxHeight?: string
  centerTitle?: boolean
}

const DialogContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3, 4),
  gap: theme.spacing(2),
  height: '100%',
  justifyContent: 'space-between'
}))

export const Header = ({ icon, title, variant = 'success' }) => {
  const theme = useTheme()
  return (
    <Stack alignSelf="center" gap={1}>
      <Stack
        justifyContent="center"
        alignItems="center"
        alignSelf="center"
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: variant === 'success' ? '#DBE8FE' : '#FEE2E2',
          color: `${variant === 'success' ? theme.palette.primary.main : theme.palette.error.main} !important`
        }}>
        {icon}
      </Stack>
      <Typography variant="h6" sx={{ fontSize: 20, fontWeight: 600 }}>
        {title}
      </Typography>
    </Stack>
  )
}

const Modal = ({
  title,
  onClose,
  children,
  header: Header,
  icon,
  height,
  maxWidth,
  minWidth = '360px',
  minHeight,
  maxHeight,
  closeIconSx,
  centerTitle = false
}: PropsWithChildren<IModalProps>) => {
  const resolvedIcon = icon ?? (title ? <i className="fa-regular fa-circle-info" /> : null)

  return (
    <BaseModal
      onClose={onClose}
      paperSx={{
        borderRadius: '16px',
        height,
        maxHeight,
        maxWidth,
        minHeight,
        minWidth
      }}
    >
      <DialogContent height={height}>
        {Boolean(onClose) && (
          <IconButton
            aria-label="Fermer"
            onClick={onClose}
            size="small"
            sx={{
              borderRadius: '50%',
              cursor: 'pointer',
              height: 32,
              padding: 0,
              position: 'absolute',
              right: 16,
              top: 16,
              width: 32,
              zIndex: 1,
              '& i': {
                lineHeight: 1,
                pointerEvents: 'none'
              },
              ...closeIconSx
            }}
          >
            <i aria-hidden="true" className="fa-light fa-xmark" style={{ fontSize: 14 }} />
          </IconButton>
        )}
        {(Boolean(resolvedIcon) || Boolean(title) || Boolean(Header)) && (
          <Box
            sx={{
              display: 'flex',
              gap: resolvedIcon ? '12px' : 0,
              alignItems: 'center',
              position: 'relative'
            }}>
            {resolvedIcon && <ActionIcon>{resolvedIcon}</ActionIcon>}
            {Header
              ? <Header />
              : Boolean(title) && (
              <Title bulletless lineless titleKey={title as string} centerTitle={centerTitle}>
              </Title>
              )}
          </Box>
        )}
        {children}
      </DialogContent>
    </BaseModal>
  )
}

export default Modal
